import * as ents from '@attain-apps/entities'
import * as graphql from '@attain-apps/fe/web/main/lib/graphql'
import { TypeChecker } from '@attain-apps/type-guards'
import gql from 'graphql-tag'

import * as enums from '../enums'

import * as comparison from './comparison'
import * as selection from './selection'

export const DOC = gql`
  ${comparison.DOC}
  ${selection.DOC}

  fragment FragmentGeneralLedgerFilterStatementElement on GeneralLedgerFilterStatementElement {
    ... on GeneralLedgerFilterNestedStatementReference {
      nestedStatementIdx
    }
    ...FragmentGeneralLedgerFilterComparison
    ...FragmentGeneralLedgerFilterSelection
    __typename
  }

  fragment FragmentGeneralLedgerFilterNestedStatement on GeneralLedgerFilterNestedStatement {
    connector
    elements {
      ...FragmentGeneralLedgerFilterStatementElement
    }
  }

  fragment FragmentGeneralLedgerFilterStatement on GeneralLedgerFilterStatement {
    statements {
      ...FragmentGeneralLedgerFilterNestedStatement
    }
    __typename
  }
`
export const TRANSFORMER = graphql.transformers.OUT.nullable(
  graphql.transformers.OUT.custom<
    graphql.schema.FragmentGeneralLedgerFilterStatementFragment,
    ents.extra.rate.gl.Filter.Statement | undefined
  >(({ statements = [] }) => {
    // NOTE: We utilize only the first element of statements because the
    // backend provides a single main statement, and the remaining elements
    // represent parts of that primary statement.
    const statement = statements?.[0]

    if (!statement) {
      return undefined
    }

    return _getStatement(statement.connector, statement.elements, statements)
  }),
)

// NOTE: The transformation of the statement is performed in this file because
// in the transformer for the nested statement fragment, we are unable to pass
// the 'statements' reference. This reference is needed due to the recursion
// within the method.
function _getStatement(
  connector: graphql.schema.GeneralLedgerFilterStatementConnector | Nullish,
  elements:
    | readonly (
        | graphql.schema.FragmentGeneralLedgerFilterStatementElementFragment
        | Nullish
      )[]
    | Nullish,
  statements:
    | readonly (
        | graphql.schema.FragmentGeneralLedgerFilterNestedStatementFragment
        | Nullish
      )[]
    | Nullish,
): ents.extra.rate.gl.Filter.Statement {
  return {
    connector:
      enums.statementConnector.TRANSFORMER.getEntityFrom(connector) ??
      ents.extra.rate.gl.Filter.Statement.Connector.And,
    filters: _getFilter(elements, statements),
  }
}

function _getFilter(
  elements:
    | readonly (
        | graphql.schema.FragmentGeneralLedgerFilterStatementElementFragment
        | Nullish
      )[]
    | Nullish,
  statements:
    | readonly (
        | graphql.schema.FragmentGeneralLedgerFilterNestedStatementFragment
        | Nullish
      )[]
    | Nullish,
): ents.extra.rate.gl.Filter[] {
  return (elements ?? [])
    .filter(TypeChecker.isNotNullish.raw)
    .map(element => {
      switch (element.__typename) {
        case 'GeneralLedgerFilterSelection':
          return selection.TRANSFORMER.getEntityFrom(element)
        case 'GeneralLedgerFilterComparison':
          return comparison.TRANSFORMER.getEntityFrom(element)
        case 'GeneralLedgerFilterNestedStatementReference': {
          const statement = TypeChecker.isNotNullish(element.nestedStatementIdx)
            ? statements?.[element.nestedStatementIdx]
            : undefined
          if (!statement) {
            return undefined
          }
          return _getStatement(
            statement.connector,
            statement.elements,
            statements,
          )
        }
      }
    })
    .filter(TypeChecker.isNotNullish.raw)
}
